import { FilledInput, FormControl, FormHelperText, Input, InputLabel, OutlinedInput, PropTypes } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import CurrencyInput from './CurrencyInput';

type Props = {
    value?: number;
    onChange(e: React.ChangeEvent<HTMLInputElement>, value: number): void;
    onBlur?(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    precision: string;
    label: string;
    disabled?: boolean;
    id?: string;
    inputType?: string;
    allowNegative?: boolean;
    error?: boolean;
    helperText?: string;
    name?: string;
    endAdornment?: React.ReactNode;
    margin?: 'dense' | 'normal' | 'none';

    decimalSeparator?: string;
    thousandSeparator?: string;
    locale?: string;

    variant?: 'standard' | 'outlined' | 'filled';
};

const MuiCurrencyInput: React.FC<Props> = (props) => {

    const [value, setValue] = useState(0);

    const parts = Intl.NumberFormat(props.locale || navigator.language).formatToParts(1000.1);
    const decimalSeparator = props.decimalSeparator || parts.find(part => part.type === 'decimal').value;
    const thousandSeparator = props.thousandSeparator || parts.find(part => part.type === 'group').value;

    function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const newValue = Number(
            e.target.value
                .split(thousandSeparator)
                .join('')
                .replace(decimalSeparator, '.')
        );

        setValue(newValue);
        props.onChange(e, newValue);
    }

    if (!props.variant || props.variant === 'standard') {
        return (
            <FormControl error={props.error} fullWidth margin={props.margin} >
                <InputLabel error={props.error} shrink id={props.label}>
                    {props.label}
                </InputLabel>
                <Input
                    name={props.name}
                    error={props.error}
                    fullWidth
                    inputComponent={CurrencyInput}
                    disabled={props.disabled}
                    onBlur={props.onBlur}
                    inputProps={{
                        id: props.id,
                        value: props.value || value,
                        disabled: props.disabled,
                        onChangeEvent: handleChange,
                        decimalSeparator: decimalSeparator,
                        thousandSeparator: thousandSeparator,
                        locale: props.locale,
                        precision: props.precision,
                        inputType: props.inputType,
                        allowNegative: props.allowNegative,
                    }}
                    endAdornment={props.endAdornment}
                />
                {props.error ? (
                    <FormHelperText error={props.error}>
                        {props.helperText}
                    </FormHelperText>
                ) : null}
            </FormControl>
        );
    } else if (props.variant === 'filled') {
        return (
            <FormControl error={props.error} fullWidth margin={props.margin} >
                <InputLabel error={props.error} shrink id={props.label}>
                    {props.label}
                </InputLabel>
                <FilledInput
                    name={props.name}
                    error={props.error}
                    fullWidth
                    inputComponent={CurrencyInput}
                    disabled={props.disabled}
                    onBlur={props.onBlur}
                    inputProps={{
                        id: props.id,
                        value: props.value || value,
                        disabled: props.disabled,
                        onChangeEvent: handleChange,
                        decimalSeparator: props.decimalSeparator,
                        thousandSeparator: props.thousandSeparator,
                        locale: props.locale,
                        precision: props.precision,
                        inputType: props.inputType,
                        allowNegative: props.allowNegative,
                        minValue: 1,
                        maxValue: 100
                    }}
                    endAdornment={props.endAdornment}
                />
                {props.error ? (
                    <FormHelperText error={props.error}>
                        {props.helperText}
                    </FormHelperText>
                ) : null}
            </FormControl>
        );
    } else {
        return (
            <FormControl error={props.error} fullWidth margin={props.margin} >
                <InputLabel error={props.error} shrink id={props.label}>
                    {props.label}
                </InputLabel>
                <OutlinedInput
                    name={props.name}
                    error={props.error}
                    notched
                    label={props.label}
                    fullWidth
                    inputComponent={CurrencyInput}
                    disabled={props.disabled}
                    onBlur={props.onBlur}
                    inputProps={{
                        id: props.id,
                        value: props.value || value,
                        disabled: props.disabled,
                        onChangeEvent: handleChange,
                        decimalSeparator: props.decimalSeparator,
                        thousandSeparator: props.thousandSeparator,
                        locale: props.locale,
                        precision: props.precision,
                        inputType: props.inputType,
                        allowNegative: props.allowNegative
                    }}
                    endAdornment={props.endAdornment}
                />
                {props.error ? (
                    <FormHelperText error={props.error}>
                        {props.helperText}
                    </FormHelperText>
                ) : null}
            </FormControl>
        );
    }
};

export default MuiCurrencyInput;